import { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Users, Calendar, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';

const services = [
  {
    icon: Building2,
    title: 'Företagsluncher',
    description: 'Daglig lunch för era medarbetare med rabatterade företagsavtal.',
    features: ['Flexibla avtal', 'Fakturering per månad', 'Vegetariska alternativ varje dag'],
  },
  {
    icon: Users,
    title: 'Konferenser',
    description: 'Lunch och fika till era möten och konferenser.',
    features: ['Buffé eller servering', 'Anpassade menyer', 'Lokal hos oss eller er'],
  },
  {
    icon: Calendar,
    title: 'Evenemang',
    description: 'Catering till firmafester, kickoffs och andra tillställningar.',
    features: ['Skräddarsydda menyer', 'Personal på plats', 'Helhetslösningar'],
  },
];

const Foretag = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.company || !formData.name || !formData.email || !formData.message) {
      toast.error('Fyll i alla obligatoriska fält');
      return;
    }

    if (formData.phone) {
      // Allow only numbers, spaces, plus, minus and parentheses. Require at least 6 digits.
      const phoneRegex = /^[0-9+\-\s()]*$/;
      const digitCount = formData.phone.replace(/\D/g, '').length;
      
      if (!phoneRegex.test(formData.phone) || digitCount < 6) {
        toast.error('Vänligen ange ett giltigt telefonnummer');
        return;
      }
    }

    try {
      const response = await fetch('https://europe-west1-dagens-l-tt.cloudfunctions.net/createCompanyRequest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      setSubmitted(true);
      toast.success('Tack! Vi återkommer inom kort.');
      
      setFormData({
        company: '',
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error("Fel vid sparande av förfrågan:", error);
      toast.error('Något gick fel. Vänligen försök igen senare.');
    }
  };

  return (
    <Layout>
      <SEO 
        title="Företag & Event"
        description="Företagsluncher, konferenser och catering i Söderhamn. Vi på Restaurang CFL skräddarsyr lösningar för ditt företag."
      />
      {/* Header */}
      <section className="bg-secondary/30 py-12 md:py-16">
        <div className="container-wide mx-auto px-4 text-center">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            Företag & Event
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Vi erbjuder skräddarsydda lösningar för företag, konferenser och evenemang.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="container-wide mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.title}
                  className="card-warm p-8 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <Icon size={28} className="text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 size={16} className="text-accent shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-secondary/30">
        <div className="container-narrow mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Kontakta oss
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Berätta om era behov så återkommer vi med ett förslag.
            </p>
          </div>

          {submitted ? (
            <div className="card-warm p-12 text-center animate-scale-in">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={32} className="text-accent" />
              </div>
              <h3 className="font-display text-2xl font-semibold text-foreground mb-3">
                Tack för ditt meddelande!
              </h3>
              <p className="text-muted-foreground mb-6">
                Vi har tagit emot din förfrågan och återkommer inom 1–2 arbetsdagar.
              </p>
              <Button onClick={() => setSubmitted(false)} variant="secondary">
                Skicka en ny förfrågan
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card-warm p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                    Företag *
                  </label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Företagsnamn AB"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    Kontaktperson *
                  </label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ditt namn"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    E-post *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="namn@foretag.se"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                    Telefon
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="070-123 45 67"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Meddelande *
                </label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Berätta om era behov, antal personer, datum..."
                  rows={5}
                />
              </div>

              <Button type="submit" size="lg" className="w-full md:w-auto">
                Skicka förfrågan
                <Send size={18} />
              </Button>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Foretag;
